import React, { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  dashboardService,
  type DoctorMetric,
  type PeriodType,
} from "../../services/dashboard.service";
import { Select } from "../ui/Select";
import { SearchableSelect } from "../ui/SearchableSelect";
import { SearchableMultiSelect } from "../ui/SearchableMultiSelect";
import { Button } from "../ui/Button";
import { DatePicker } from "../ui/DatePicker";

// Helpers for date formatting and defaults
const formatDate = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = now; // today
  return { startDate: formatDate(start), endDate: formatDate(end) };
};

type SortField = "provider" | "charges" | "payments";
type SortDirection = "asc" | "desc" | null;

export const ChargesPaymentsWidget: React.FC = () => {
  const [periodType, setPeriodType] = useState<PeriodType>("current_month");
  const [selectedJCode, setSelectedJCode] = useState<string>("all");
  const [dateRange, setDateRange] = useState(() => getDefaultMonthRange());
  const [appliedDateRange, setAppliedDateRange] = useState(() => getDefaultMonthRange());
  const [selectedDoctors, setSelectedDoctors] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField | null>("charges");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const initializedSelectionRef = useRef(false);

  // Compute start/end date from selector (backend expects explicit dates; no period param)
  const { startDate, endDate } = useMemo(() => {
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    if (periodType === "custom") {
      return { startDate: appliedDateRange.startDate, endDate: appliedDateRange.endDate };
    }

    if (periodType === "current_week") {
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday, go back 6 days
      const monday = new Date(now);
      monday.setDate(now.getDate() - daysToMonday);
      return { startDate: fmt(monday), endDate: fmt(now) };
    }

    if (periodType === "previous_week") {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const currentMonday = new Date(now);
      currentMonday.setDate(now.getDate() - daysToMonday);

      const prevMonday = new Date(currentMonday);
      prevMonday.setDate(currentMonday.getDate() - 7);

      const prevSunday = new Date(currentMonday);
      prevSunday.setDate(currentMonday.getDate() - 1);

      return { startDate: fmt(prevMonday), endDate: fmt(prevSunday) };
    }

    if (periodType === "previous_month") {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    if (periodType === "year_to_date") {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1); // January 1st of current year
      return { startDate: fmt(start), endDate: fmt(now) };
    }

    if (periodType === "last_year") {
      const now = new Date();
      const start = new Date(now.getFullYear() - 1, 0, 1); // January 1st of last year
      const end = new Date(now.getFullYear() - 1, 11, 31); // December 31st of last year
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    if (periodType === "last_90_days") {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 90);
      return { startDate: fmt(start), endDate: fmt(now) };
    }

    // current_month default: 1st of current month to today
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { startDate: fmt(start), endDate: fmt(now) };
  }, [periodType, appliedDateRange]);

  // Fetch Charges & Payments metrics (practice total + matrix by doctors)
  const {
    data: chargesMetrics,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["charges-payments", startDate, endDate],
    queryFn: () => dashboardService.getChargesPayments(startDate, endDate),
  });

  // Fetch J-Codes totals for the selected date range and optional jcode
  const { data: jcodesMetrics = [] } = useQuery({
    queryKey: ["jcodes-charges", startDate, endDate, selectedJCode],
    queryFn: () =>
      dashboardService.getJCodesCharges(
        startDate,
        endDate,
        selectedJCode !== "all" ? selectedJCode : undefined
      ),
  });

  // Fetch J-Codes list (for selector)
  const { data: jCodes = [] } = useQuery({
    queryKey: ["jcodes"],
    queryFn: () => dashboardService.getJCodes(),
  });

  // Parse metrics from separate endpoints
  const { practiceTotal, doctorMetrics, jCodesTotal } = useMemo(() => {
    let practiceTotal = { charges: "0", payments: "0" };
    let doctorMetrics: DoctorMetric[] = [];
    let jCodesTotal = { charges: "0", payments: "0" };

    // Charges endpoint parsing
    chargesMetrics?.forEach((metric) => {
      if (metric.metricName === "Practice Total Charges") {
        const value = metric.value?.toString() || "0";
        practiceTotal.charges = value;
      } else if (metric.metricName === "Practice Total Payments") {
        const value = metric.value?.toString() || "0";
        practiceTotal.payments = value;
      } else if (metric.metricName === "Matrix By Doctors" && Array.isArray(metric.value)) {
        doctorMetrics = metric.value as DoctorMetric[];
      }
    });

    // J-Codes totals parsing
    jcodesMetrics?.forEach((metric) => {
      if (metric.metricName === "JCodes Charges") {
        jCodesTotal.charges = metric.value?.toString() || "0";
      } else if (metric.metricName === "JCodes Payments") {
        jCodesTotal.payments = metric.value?.toString() || "0";
      }
    });

    return { practiceTotal, doctorMetrics, jCodesTotal };
  }, [chargesMetrics, jcodesMetrics]);

  // Initialize doctor selection
  useEffect(() => {
    if (doctorMetrics.length === 0) return;

    if (!initializedSelectionRef.current) {
      initializedSelectionRef.current = true;
      setSelectedDoctors(doctorMetrics.map((d) => d.doctor));
      return;
    }

    // Ensure selection doesn't include removed doctors on subsequent updates
    setSelectedDoctors((prev) =>
      prev.filter((name) => doctorMetrics.some((d) => d.doctor === name))
    );
  }, [doctorMetrics]);

  // Get all doctor names
  const allDoctorNames = doctorMetrics
    .map((d) => d.doctor)
    .filter((name): name is string => name != null);

  // Filter and sort doctors based on selection
  const filteredDoctorMetrics = useMemo(() => {
    if (selectedDoctors.length === 0) return [];
    const filtered = doctorMetrics.filter((d) => selectedDoctors.includes(d.doctor));

    // If no sorting is applied, return original order
    if (!sortField || !sortDirection) {
      return filtered;
    }

    // Sort the filtered results
    return [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (sortField === "provider") {
        aValue = a.doctor;
        bValue = b.doctor;
      } else if (sortField === "charges") {
        aValue = parseFloat(a.charges) || 0;
        bValue = parseFloat(b.charges) || 0;
      } else {
        aValue = parseFloat(a.payments) || 0;
        bValue = parseFloat(b.payments) || 0;
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [doctorMetrics, selectedDoctors, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatCurrency = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return "$0";
    return `$${num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  return (
    <div className="bg-white border-2 border-border-gray flex flex-col">
      {/* Header */}
      <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
        <h3 className="font-bold text-lg">Charges | Payments</h3>
        <span className="text-xs sm:text-sm">Billing and Payment Trends</span>
      </div>

      {/* Filters */}
      <div className="p-4 bg-gray-50 border-b-2 border-border-gray flex flex-wrap gap-3 items-end">
        <div className="flex-shrink-0 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
          <Select
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value as PeriodType)}
            className="w-full"
          >
            <option value="current_week">Current Week</option>
            <option value="previous_week">Previous Week</option>
            <option value="current_month">Current Month</option>
            <option value="previous_month">Previous Month</option>
            <option value="last_90_days">Last 90 Days</option>
            <option value="year_to_date">Year to Date</option>
            <option value="last_year">Last Year</option>
            <option value="custom">Custom Period</option>
          </Select>
        </div>

        {periodType === "custom" && (
          <>
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">Start date</label>
              <DatePicker
                value={dateRange.startDate}
                maxDate={dateRange.endDate || undefined}
                onChange={(value) =>
                  setDateRange((prev: { startDate: string; endDate: string }) => ({
                    ...prev,
                    startDate: value,
                  }))
                }
              />
            </div>
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">End date</label>
              <DatePicker
                value={dateRange.endDate}
                minDate={dateRange.startDate || undefined}
                onChange={(value) =>
                  setDateRange((prev: { startDate: string; endDate: string }) => ({
                    ...prev,
                    endDate: value,
                  }))
                }
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const defaultRange = getDefaultMonthRange();
                setDateRange(defaultRange);
                setAppliedDateRange(defaultRange);
              }}
              className="h-10 px-3 bg-black text-white text-xs sm:text-sm hover:bg-gray-800 whitespace-nowrap flex-shrink-0"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => setAppliedDateRange(dateRange)}
              className="h-10 px-3 bg-primary-blue text-white text-xs sm:text-sm hover:bg-indigo-darker-blue whitespace-nowrap flex-shrink-0"
            >
              Apply
            </button>
          </>
        )}
      </div>

      {/* Content */}
      <div>
        {isLoading && (
          <div className="text-center py-8 text-gray-500 p-4 sm:p-6">
            Loading charges and payments data...
          </div>
        )}

        {error && (
          <div className="text-center py-8 text-red-600 p-4 sm:p-6">
            Failed to load charges and payments data. Please try again.
          </div>
        )}

        {!isLoading && !error && (
          <div className="space-y-0">
            {/* Practice Total Dropdown */}
            <div>
              <button className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors">
                <span className="font-bold">Practice Total</span>
              </button>
              <div className="px-4 py-3 bg-white border-t-2 border-border-gray">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-600 uppercase mb-1">Charges</div>
                    <div className="text-xl font-bold">{formatCurrency(practiceTotal.charges)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 uppercase mb-1">Payments</div>
                    <div className="text-xl font-bold">
                      {formatCurrency(practiceTotal.payments)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Doctors Table Dropdown */}
            {doctorMetrics.length > 0 && (
              <div className="border-t-2 border-b-2 border-border-gray">
                <div className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 border-b-2 border-border-gray">
                  <span className="font-bold">Providers</span>
                  <SearchableMultiSelect
                    options={allDoctorNames}
                    selectedOptions={selectedDoctors}
                    onSelectionChange={setSelectedDoctors}
                    label="Providers"
                    showArrow={false}
                  />
                </div>
                {filteredDoctorMetrics.length > 0 ? (
                  <div className="max-h-[400px] overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b-2 border-border-gray sticky top-0 z-10">
                        <tr>
                          <th
                            className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort("provider")}
                          >
                            <div className="flex items-center justify-between">
                              <span>Provider</span>
                              {sortField === "provider" && sortDirection && (
                                <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
                              )}
                            </div>
                          </th>
                          <th
                            className="px-4 py-2 text-right text-xs font-bold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort("charges")}
                          >
                            <div className="flex items-center justify-end">
                              <span>Charges</span>
                              {sortField === "charges" && sortDirection && (
                                <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
                              )}
                            </div>
                          </th>
                          <th
                            className="px-4 py-2 text-right text-xs font-bold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort("payments")}
                          >
                            <div className="flex items-center justify-end">
                              <span>Payments</span>
                              {sortField === "payments" && sortDirection && (
                                <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
                              )}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-gray">
                        {filteredDoctorMetrics.map((doctor, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm font-medium">{doctor.doctor}</td>
                            <td className="px-4 py-2 text-sm text-right">
                              {formatCurrency(doctor.charges)}
                            </td>
                            <td className="px-4 py-2 text-sm text-right">
                              {formatCurrency(doctor.payments)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center text-gray-500">No doctors selected</div>
                )}
              </div>
            )}

            {/* J-Codes Dropdown */}
            <div>
              <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3 bg-gray-50 border-b-2 border-border-gray">
                <span className="font-bold">J-Codes</span>
                <SearchableSelect
                  options={jCodes}
                  value={selectedJCode}
                  onChange={setSelectedJCode}
                  placeholder="Select J-Code"
                  allOptionLabel="All J-Codes"
                />
              </div>
              <div className="px-4 py-3 bg-white">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-600 uppercase mb-1">Charges</div>
                    <div className="text-xl font-bold">{formatCurrency(jCodesTotal.charges)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 uppercase mb-1">Payments</div>
                    <div className="text-xl font-bold">{formatCurrency(jCodesTotal.payments)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
